import Component from '@glimmer/component';
import { t } from 'ember-intl';
import {
  HdsCardContainer,
  HdsBadge,
} from '@hashicorp/design-system-components/components';
import { DetailSection, DefinitionList } from 'core/components';
import { optionForSourceState } from 'core/helpers';
import { boxPadding } from 'core/modifiers';

/**
 *
 * `PageHvnsCardsHvnDetails` this renders the details of an HVN.
 *
 *
 * ```
 * <Page::Hvns::Cards::HvnDetails
 *   @network={{network}}
 * />
 * ```
 *
 * @class PageHvnsCardsHvnDetails
 *
 */
export default class PageHvnsCardsHvnDetails extends Component {
  /**
   * `network` is an HVN object
   * @argument network
   * @type {object}
   */
  <template>
    <div
      class="hvns-cards-hvn-details"
      data-test-hvns-cards-hvn-details
      {{boxPadding "0 0 lg 0"}}
    >
      <HdsCardContainer @level="mid" @hasBorder="true">
        <div {{boxPadding "lg lg lg lg"}}>
          <DetailSection
            @title={{t "components.page.hvns.cards.hvn-details.title"}}
          >
            <DefinitionList {{boxPadding "md 0 xl 0"}} as |DL|>
              <DL.Key data-test-hvns-cards-hvn-details-network-id-label>
                {{t "components.page.hvns.cards.hvn-details.labels.network-id"}}
              </DL.Key>
              <DL.Value data-test-hvns-cards-hvn-details-network-id-value>
                {{@network.id}}
              </DL.Value>
              <DL.Key data-test-hvns-cards-hvn-details-network-provider-label>
                {{t
                  "components.page.hvns.cards.hvn-details.labels.cloud-provider"
                }}
              </DL.Key>
              <DL.Value data-test-hvns-cards-hvn-details-network-provider-value>
                {{#let
                  (optionForSourceState
                    "region" @network.location.region.provider
                  )
                  as |option|
                }}
                  <HdsBadge
                    @text={{t option.text}}
                    @color={{option.color}}
                    @icon={{option.icon}}
                    @isIconOnly={{option.isIconOnly}}
                    @size={{option.size}}
                    @type={{option.type}}
                  />
                {{/let}}
              </DL.Value>
              <DL.Key data-test-hvns-cards-hvn-details-network-region-label>
                {{t "components.page.hvns.cards.hvn-details.labels.region"}}
              </DL.Key>
              <DL.Value data-test-hvns-cards-hvn-details-network-region-value>
                {{@network.location.region.region}}
              </DL.Value>
              <DL.Key data-test-hvns-cards-hvn-details-network-status-label>
                {{t "components.page.hvns.cards.hvn-details.labels.status"}}
              </DL.Key>
              <DL.Value data-test-hvns-cards-hvn-details-network-status-value>
                {{#let (optionForSourceState "hvn" @network.state) as |option|}}
                  <HdsBadge
                    @text={{t option.text}}
                    @color={{option.color}}
                    @icon={{option.icon}}
                    @isIconOnly={{option.isIconOnly}}
                    @size={{option.size}}
                    @type={{option.type}}
                  />
                {{/let}}
              </DL.Value>
              <DL.Key data-test-hvns-cards-hvn-details-network-cidr-label>
                {{t "components.page.hvns.cards.hvn-details.labels.cidr-block"}}
              </DL.Key>
              <DL.Value data-test-hvns-cards-hvn-details-network-cidr-value>
                {{@network.cidrBlock}}
              </DL.Value>
            </DefinitionList>
          </DetailSection>
        </div>
      </HdsCardContainer>
    </div>
  </template>
}
